import { getRouteApi, useNavigate } from '@tanstack/react-router';
import * as React from 'react';

import type {
  NegotiationEntryStateDto,
  NegotiationHistoryFilterDto,
} from '@/api/negotiation-client';
import { NEGOTIATION_STATE_ORDER } from '@/lib/negotiation-view';
import { toStringList } from '@/lib/search-list';
import {
  NegotiationScreen,
  type NegotiationSearchPatch,
} from '@/routes/admin/campaigns/negotiation-screen';

// =============================================================================
// negotiation-route.tsx — Adaptateur routeur de la page « Négociation » (refonte, lot N4).
// =============================================================================
// L'id de campagne est le path param d'URL (« /admin/campaigns/$campaignId/negotiation ») — ressource
// d'URL, cohérente avec le contrat API (campaignId server-set, invariant n°1).
//
// TOUT l'état de vue (filtres fournisseur / profil / état, case « Afficher les verrouillées », onglet et
// page de l'historique) vit dans les SEARCH PARAMS (RULE_REF_006 : aucun localStorage pour l'état
// métier ou de vue) : une vue filtrée est PARTAGEABLE et REJOUABLE à l'identique. Chaque changement
// PRÉSERVE les autres paramètres ; une valeur remise au défaut DISPARAÎT de l'URL.
//
// Le composant de présentation reste testable hors routeur.

const routeApi = getRouteApi('/admin/campaigns/$campaignId/negotiation');

/**
 * Forme des search params préservés/mutés à la navigation (état de vue d'URL).
 *
 * LOT F1 (GATE_HUMAN 2026-09-21) : `fournisseur`, `profil` et `etat` sont des LISTES (même encodage
 * que `state`/`tokenStatus` du registre fournisseurs). Une liste VIDE fait disparaître la clé.
 */
export interface NegotiationSearch {
  fournisseur?: readonly string[];
  profil?: readonly string[];
  etat?: readonly NegotiationEntryStateDto[];
  verrouillees?: boolean;
  hist?: NegotiationHistoryFilterDto;
  page?: number;
}

export function NegotiationRoute(): React.JSX.Element {
  const { campaignId } = routeApi.useParams();
  const { fournisseur, profil, etat, verrouillees, hist, page } = routeApi.useSearch();
  const navigate = useNavigate();

  const onChangeSearch = React.useCallback(
    (patch: NegotiationSearchPatch): void => {
      void navigate({
        to: '/admin/campaigns/$campaignId/negotiation',
        params: { campaignId },
        // `prev` est l'UNION GLOBALE des search params du routeur (des clés homonymes vivent sur
        // d'autres routes) : on la lit en enregistrement opaque, on fusionne, puis on renvoie la
        // forme de CETTE route.
        search: (prev: Record<string, unknown>): NegotiationSearch => {
          const next: Record<string, unknown> = { ...prev };
          for (const [key, value] of Object.entries(patch)) {
            // Nettoyage : une clé passée à `undefined` — ou à une LISTE VIDE, ce qu'émet un filtre
            // multi-valeurs effacé — DISPARAÎT de l'URL (aucun « ?profil= » vide, aucune sentinelle).
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
              delete next[key];
            } else {
              next[key] = value;
            }
          }
          return next as NegotiationSearch;
        },
      });
    },
    [navigate, campaignId],
  );

  return (
    <NegotiationScreen
      campaignId={campaignId}
      // Garde-fou RUNTIME (cf. `lib/search-list.ts`) : une clé écartée par `validateSearch` n'est PAS
      // retirée de la search fusionnée. L'état est en plus ramené à son échelle FERMÉE (fail-closed).
      supplierSirets={toStringList(fournisseur)}
      profileCodes={toStringList(profil)}
      states={toStringList(etat, NEGOTIATION_STATE_ORDER)}
      showLocked={verrouillees}
      historyFilter={hist}
      historyPage={page}
      onChangeSearch={onChangeSearch}
    />
  );
}
